import React, { useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { WAITLIST } from "../../constants/testIds";
import { readSource } from "../../lib/track";
import {
  WAITLIST_SERVICES,
  WAITLIST_TIMING,
  WAITLIST_CONTACT_METHODS,
} from "../../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WaitlistForm({ compact = false }) {
  const location = useLocation();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    preferred_contact: "Email",
    services: [],
    preferred_timing: [],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const toggleArr = (key, value) => {
    setForm((f) => {
      const has = f[key].includes(value);
      return {
        ...f,
        [key]: has ? f[key].filter((x) => x !== value) : [...f[key], value],
      };
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please share your name, email, and phone so we can reach out.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, {
        ...form,
        ...readSource(location.search, location.pathname),
      });
      setSuccess(true);
      toast.success("You're on the list. We'll be in touch within 24 hours.");
      setForm({
        name: "",
        email: "",
        phone: "",
        preferred_contact: "Email",
        services: [],
        preferred_timing: [],
        message: "",
      });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid={WAITLIST.successMessage}
        className="border border-umber/15 bg-paper/70 rounded-sm p-10 md:p-14 text-center"
      >
        <div className="font-meta text-forest mb-4">Thanks, we've got it</div>
        <p className="font-display text-3xl md:text-4xl text-umber leading-snug max-w-xl mx-auto">
          We'll reach out within 24 hours to help you find a time.
          We're looking forward to meeting you.
        </p>
        <button
          type="button"
          onClick={() => setSuccess(false)}
          className="mt-8 text-[12px] tracking-[0.18em] uppercase text-forest underline underline-offset-4 hover:text-sage transition-colors"
        >
          Add another person
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid={WAITLIST.form}
      className={`grid grid-cols-1 ${compact ? "" : "md:grid-cols-2"} gap-x-10 gap-y-7`}
    >
      <div className={compact ? "" : "md:col-span-1"}>
        <label className="font-meta text-umber/60 block mb-2">Name</label>
        <input
          data-testid={WAITLIST.name}
          type="text"
          className="alae-input"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Your full name"
          required
        />
      </div>
      <div className={compact ? "" : "md:col-span-1"}>
        <label className="font-meta text-umber/60 block mb-2">Email</label>
        <input
          data-testid={WAITLIST.email}
          type="email"
          className="alae-input"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="you@example.com"
          required
        />
      </div>
      <div className={compact ? "" : "md:col-span-1"}>
        <label className="font-meta text-umber/60 block mb-2">Phone</label>
        <input
          data-testid={WAITLIST.phone}
          type="tel"
          className="alae-input"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          placeholder="(905) 000-0000"
          required
        />
      </div>
      <div className={compact ? "" : "md:col-span-1"}>
        <label className="font-meta text-umber/60 block mb-2">
          Preferred Contact
        </label>
        <div
          data-testid={WAITLIST.contactMethod}
          className="flex gap-3 pt-2 flex-wrap"
        >
          {WAITLIST_CONTACT_METHODS.map((m) => (
            <button
              type="button"
              key={m}
              data-active={form.preferred_contact === m}
              data-testid={`${WAITLIST.contactMethod}-${m.toLowerCase()}`}
              className="chip"
              onClick={() => setForm({ ...form, preferred_contact: m })}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      <div className="md:col-span-2">
        <label className="font-meta text-umber/60 block mb-3">
          Services you're interested in
        </label>
        <div
          data-testid={WAITLIST.services}
          className="flex flex-wrap gap-2.5"
        >
          {WAITLIST_SERVICES.map((s) => (
            <button
              type="button"
              key={s}
              data-active={form.services.includes(s)}
              data-testid={`${WAITLIST.services}-${s
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")}`}
              className="chip"
              onClick={() => toggleArr("services", s)}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="md:col-span-2">
        <label className="font-meta text-umber/60 block mb-3">
          Preferred Timing
        </label>
        <div data-testid={WAITLIST.timing} className="flex flex-wrap gap-2.5">
          {WAITLIST_TIMING.map((t) => (
            <button
              type="button"
              key={t}
              data-active={form.preferred_timing.includes(t)}
              data-testid={`${WAITLIST.timing}-${t.toLowerCase()}`}
              className="chip"
              onClick={() => toggleArr("preferred_timing", t)}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="md:col-span-2 flex flex-col md:flex-row md:items-center md:justify-between gap-6 pt-4">
        <p className="text-xs text-umber/60 max-w-md leading-relaxed">
          By joining the waitlist you agree to be contacted about your
          enquiry. We never share your details.
        </p>
        <button
          type="submit"
          data-testid={WAITLIST.submit}
          disabled={submitting}
          className="bg-forest text-paper rounded-full px-10 py-3.5 text-[12px] tracking-[0.22em] uppercase hover:bg-sage transition-all duration-300 disabled:opacity-60"
        >
          {submitting ? "Sending…" : "Request a Callback"}
        </button>
      </div>
    </form>
  );
}
